import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { rewardApi } from '../../api/reward.api';
import { ActivityTrendItem } from '../../types/Reward.api.type';

type PeriodOption = '오늘' | '시간대별' | '7D' | '30D' | 'ALL';

const PERIOD_TABS: { value: PeriodOption; label: string }[] = [
  { value: '오늘', label: '오늘' },
  { value: '시간대별', label: '시간대별' },
  { value: '7D', label: '7일' },
  { value: '30D', label: '30일' },
  { value: 'ALL', label: '전체' },
];

interface ActivityTrendChartProps {
  title: string;
  rewardType?: string;
  color: string;
  apiFn?: (period: string) => Promise<any>;
}

export const ActivityTrendChart = ({ title, rewardType, color, apiFn }: ActivityTrendChartProps) => {
  const [period, setPeriod] = useState<PeriodOption>('오늘');

  const isHourly = period === '시간대별' || period === '오늘';
  const apiPeriod = period === '오늘' ? 'TODAY' : period === '시간대별' ? 'HOURLY_ALL' : period;

  const { data, isLoading } = useQuery({
    queryKey: apiFn ? ['activityTrend', title, period] : ['activityTrend', rewardType, period],
    queryFn: () =>
      apiFn
        ? apiFn(apiPeriod).then(res => res.data)
        : rewardApi.getActivityTrend(rewardType!, apiPeriod).then(res => res.data.results),
  });

  const chartData: ActivityTrendItem[] = isHourly
    ? (() => {
        const hourlyRaw: ActivityTrendItem[] = data?.hourly ?? [];
        const hourMap = new Map(hourlyRaw.map(item => [item.date, item.count]));
        return Array.from({ length: 24 }, (_, h) => ({
          date: `${h}시`,
          count: hourMap.get(`${h}시`) ?? 0,
        }));
      })()
    : (data?.daily ?? []).map((item: ActivityTrendItem) => ({
        ...item,
        date: item.date.slice(5),
      }));

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        <h3 className="text-lg font-semibold text-[#030213] mb-4">{title}</h3>
        <div className="h-[300px] flex items-center justify-center">
          <p className="text-[#6B7280]">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#030213]">{title}</h3>
        <div className="flex gap-2">
          {PERIOD_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setPeriod(tab.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                period === tab.value
                  ? `text-white`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={period === tab.value ? { backgroundColor: color } : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        {chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-[#6B7280]">데이터가 없습니다.</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} width={45} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={color}
                strokeWidth={2}
                dot={{ fill: color, r: 4 }}
                activeDot={{ r: 6 }}
                name={title}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
